import * as React from 'react'
import { Check, Minus } from 'lucide-react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'

import { cn } from '@ui/lib/utils'
import { VariantProps, cva } from 'class-variance-authority'

const checkboxVariants = cva('relative overflow-hidden ', {
  variants: {
    size: {
      default: 'size-xs',
    },
    variant: {
      default:
        'peer shrink-0 rounded-sm border ' +
        'disabled:cursor-not-allowed disabled:!border-0 disabled:!bg-gray-100',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
})

type CheckboxProps = React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> &
  VariantProps<typeof checkboxVariants> & {
    iconType?: 'check' | 'minus'
  }

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, size, variant, iconType, ...props }, ref) => {
  const isChecked = props.checked
  const [initialValue] = React.useState(isChecked)
  const [isEdited, setIsEdited] = React.useState(false)

  React.useEffect(() => {
    if (initialValue !== isChecked) setIsEdited(true)
  }, [isChecked])

  const Icon = (props: React.SVGProps<SVGSVGElement>) =>
    iconType === 'minus' ? <Minus {...props} /> : <Check {...props} />

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={cn(
        checkboxVariants({ size, variant }),
        'transition-[border] duration-100 ease-linear',
        isChecked ? 'border-0' : 'border-gray-300 hover:border-2',
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          'transform-origin-center absolute left-0 top-0 size-full rounded-full bg-blue-500',
          isChecked ? 'scale-[1.2]' : 'scale-0',
          {
            'animate-fill-in': isChecked && isEdited,
            'animate-fill-out': !isChecked && isEdited,
          },
        )}
      />
      <CheckboxPrimitive.Indicator className="relative flex items-center justify-center text-current">
        <Icon
          className="size-xxxs"
          color={props.disabled ? 'var(--gray-300)' : 'white'}
        />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  )
})
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
